import { useState, useEffect, useCallback } from 'react'
import {
  Factory, Plus, Save, Trash2, ChevronRight, MapPin, Package, Percent, Gauge,
  Landmark, FileText, Plug, BarChart3, History, X, RefreshCw,
} from 'lucide-react'
import api from '../api/client'
import { UFS } from '../constants/uf'

const inp = 'w-full rounded-lg border border-neutral-200 bg-white px-3 py-2 text-sm outline-none focus:border-accent-500'
const lbl = 'block text-[11px] font-semibold text-neutral-500 uppercase tracking-wide mb-1'
const card = 'bg-white rounded-2xl border border-neutral-200 shadow-sm'
const money = (v) => 'R$ ' + Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })

const TIPO_FATURA = [['unificada', 'Fatura Unificada'], ['duas', 'Duas Faturas'], ['ambas', 'Ambas']]
const TIPOS_CLIENTE = [['pf', 'Pessoa Física'], ['pj', 'Pessoa Jurídica'], ['condominio', 'Condomínio']]
const PRODUTOS_PADRAO = [
  { chave: 'gd', nome: 'Energia por Assinatura (GD)' },
  { chave: 'mercado_livre', nome: 'Mercado Livre' },
]

function Secao({ icon: Icon, titulo, children, aberta = false }) {
  const [open, setOpen] = useState(aberta)
  return (
    <div className={card + ' overflow-hidden'}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-2 px-4 py-3 text-left">
        <Icon size={16} className="text-accent-500" />
        <span className="font-heading font-bold text-graphite text-sm flex-1">{titulo}</span>
        <ChevronRight size={16} className={`text-neutral-400 transition-transform ${open ? 'rotate-90' : ''}`} />
      </button>
      {open && <div className="px-4 pb-4 border-t border-neutral-100 pt-3">{children}</div>}
    </div>
  )
}

export default function GeradorParceiro() {
  const [lista, setLista] = useState([])
  const [sel, setSel] = useState(null)      // gerador em edição (objeto completo)
  const [dash, setDash] = useState(null)
  const [msg, setMsg] = useState('')
  const [busy, setBusy] = useState(false)

  const carregar = useCallback(async () => {
    const { data } = await api.get('/geradoras?limit=200')
    setLista(data.data || [])
  }, [])
  useEffect(() => { carregar() }, [carregar])

  async function abrir(id) {
    setMsg(''); setDash(null)
    if (!id) { setSel(vazio()); return }
    const { data } = await api.get(`/geradoras/${id}`)
    setSel(data)
  }
  function vazio() {
    return {
      nome: '', razao_social: '', nome_fantasia: '', cnpj: '', inscricao_estadual: '',
      responsavel_comercial: '', email: '', telefone: '', site: '', status: 'ativa',
      estados_atendidos: [], produtos: [], regras_estado: [], faixas_consumo: [],
      operacional: { tipo_fatura: 'unificada', tipos_cliente: ['pf', 'pj', 'condominio'] },
      financeiro: {}, fiscal_nf: {}, api_config: { ambiente: 'producao' },
    }
  }
  const set = (k, v) => setSel((s) => ({ ...s, [k]: v }))
  const setObj = (obj, k, v) => setSel((s) => ({ ...s, [obj]: { ...(s[obj] || {}), [k]: v } }))

  function toggleUF(uf) {
    const cur = sel.estados_atendidos || []
    set('estados_atendidos', cur.includes(uf) ? cur.filter((x) => x !== uf) : [...cur, uf])
  }
  function toggleTipoCli(t) {
    const cur = sel.operacional?.tipos_cliente || []
    setObj('operacional', 'tipos_cliente', cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t])
  }
  function addProduto(p) {
    if ((sel.produtos || []).some((x) => x.chave === p.chave)) return
    set('produtos', [...(sel.produtos || []), { ...p, ativo: true }])
  }
  // Regras por Estado
  const addRegra = () => set('regras_estado', [...(sel.regras_estado || []), {
    estado: '', tipo_acordo: '', desconto_maximo: '', desconto_cliente: '', part_theconect: '',
    part_gerador: '', permite_campanhas: true, permite_desconto_personalizado: false,
    vigencia_inicial: '', vigencia_final: '', status: 'ativa',
  }])
  const setRegra = (i, k, v) => set('regras_estado', sel.regras_estado.map((r, j) => j === i ? { ...r, [k]: v } : r))
  const delRegra = (i) => set('regras_estado', sel.regras_estado.filter((_, j) => j !== i))
  // Faixas de consumo
  const addFaixa = () => set('faixas_consumo', [...(sel.faixas_consumo || []), { min_kwh: '', max_kwh: '', desconto_pct: '' }])
  const setFaixa = (i, k, v) => set('faixas_consumo', sel.faixas_consumo.map((f, j) => j === i ? { ...f, [k]: v } : f))
  const delFaixa = (i) => set('faixas_consumo', sel.faixas_consumo.filter((_, j) => j !== i))

  async function salvar() {
    if (!sel.nome?.trim()) { setMsg('Informe o nome do gerador.'); return }
    setBusy(true); setMsg('')
    try {
      if (sel.id) await api.put(`/geradoras/${sel.id}`, sel)
      else { const { data } = await api.post('/geradoras', sel); setSel(data) }
      await carregar()
      setMsg('Gerador salvo com sucesso.')
    } catch (e) { setMsg(e?.response?.data?.detail || 'Erro ao salvar.') } finally { setBusy(false) }
  }
  async function excluir() {
    if (!sel.id || !confirm('Excluir este gerador parceiro?')) return
    await api.delete(`/geradoras/${sel.id}`); setSel(null); carregar()
  }
  async function verDashboard() {
    if (!sel.id) return
    const { data } = await api.get(`/geradoras/${sel.id}/dashboard`); setDash(data)
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center gap-2 mb-5">
        <Factory className="text-accent-500" size={22} />
        <h1 className="text-xl font-heading font-extrabold text-graphite flex-1">Geradores Parceiros</h1>
        <button onClick={() => abrir(null)} className="inline-flex items-center gap-1.5 rounded-xl text-white font-bold px-4 py-2 text-sm" style={{ background: 'linear-gradient(135deg,#ffcc00,#ff6c00)' }}>
          <Plus size={16} /> Novo Gerador
        </button>
      </div>

      {!sel && (
        <div className={card + ' divide-y divide-neutral-100'}>
          {lista.length === 0 && <p className="p-6 text-sm text-neutral-500">Nenhum gerador cadastrado.</p>}
          {lista.map((g) => (
            <button key={g.id} onClick={() => abrir(g.id)} className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-neutral-50">
              <div className="w-9 h-9 rounded-lg bg-accent-50 flex items-center justify-center"><Factory size={16} className="text-accent-500" /></div>
              <div className="flex-1">
                <p className="font-semibold text-graphite text-sm">{g.nome}</p>
                <p className="text-xs text-neutral-500">{(g.estados_atendidos || []).join(', ') || 'sem estados'} · {(g.produtos || []).length} produto(s)</p>
              </div>
              <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded-full ${g.status === 'ativa' ? 'bg-emerald-50 text-emerald-600' : 'bg-neutral-100 text-neutral-500'}`}>{g.status}</span>
              <ChevronRight size={16} className="text-neutral-400" />
            </button>
          ))}
        </div>
      )}

      {sel && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <button onClick={() => setSel(null)} className="text-sm text-neutral-500 hover:text-graphite inline-flex items-center gap-1"><X size={15} /> Fechar</button>
            <div className="flex items-center gap-2">
              {sel.id && <button onClick={excluir} className="text-sm text-red-600 inline-flex items-center gap-1 px-3 py-1.5 rounded-lg hover:bg-red-50"><Trash2 size={15} /> Excluir</button>}
              <button onClick={salvar} disabled={busy} className="inline-flex items-center gap-1.5 rounded-xl text-white font-bold px-4 py-2 text-sm disabled:opacity-60" style={{ background: 'linear-gradient(135deg,#ffcc00,#ff6c00)' }}><Save size={15} /> {busy ? 'Salvando...' : 'Salvar'}</button>
            </div>
          </div>
          {msg && <div className="p-3 rounded-xl bg-accent-50 border border-accent-200 text-accent-800 text-sm">{msg}</div>}

          <Secao icon={Factory} titulo="Dados Gerais" aberta>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><label className={lbl}>Nome do Gerador</label><input className={inp} value={sel.nome || ''} onChange={(e) => set('nome', e.target.value)} /></div>
              <div><label className={lbl}>Razão Social</label><input className={inp} value={sel.razao_social || ''} onChange={(e) => set('razao_social', e.target.value)} /></div>
              <div><label className={lbl}>Nome Fantasia</label><input className={inp} value={sel.nome_fantasia || ''} onChange={(e) => set('nome_fantasia', e.target.value)} /></div>
              <div><label className={lbl}>CNPJ</label><input className={inp} value={sel.cnpj || ''} onChange={(e) => set('cnpj', e.target.value)} /></div>
              <div><label className={lbl}>Inscrição Estadual</label><input className={inp} value={sel.inscricao_estadual || ''} onChange={(e) => set('inscricao_estadual', e.target.value)} /></div>
              <div><label className={lbl}>Responsável Comercial</label><input className={inp} value={sel.responsavel_comercial || ''} onChange={(e) => set('responsavel_comercial', e.target.value)} /></div>
              <div><label className={lbl}>E-mail</label><input className={inp} value={sel.email || ''} onChange={(e) => set('email', e.target.value)} /></div>
              <div><label className={lbl}>Telefone</label><input className={inp} value={sel.telefone || ''} onChange={(e) => set('telefone', e.target.value)} /></div>
              <div><label className={lbl}>Site</label><input className={inp} value={sel.site || ''} onChange={(e) => set('site', e.target.value)} /></div>
              <div><label className={lbl}>Status</label>
                <select className={inp} value={sel.status || 'ativa'} onChange={(e) => set('status', e.target.value)}>
                  <option value="ativa">Ativo</option><option value="standby">Stand by</option><option value="inativa">Inativo</option><option value="encerrada">Encerrado</option>
                </select>
              </div>
            </div>
          </Secao>

          <Secao icon={MapPin} titulo={`Estados Atendidos (${(sel.estados_atendidos || []).length})`}>
            <div className="flex flex-wrap gap-1.5">
              {UFS.map((uf) => {
                const on = (sel.estados_atendidos || []).includes(uf)
                return <button key={uf} onClick={() => toggleUF(uf)} className={`px-2.5 py-1 rounded-lg text-xs font-bold border ${on ? 'bg-accent-500 text-white border-accent-500' : 'bg-white text-neutral-500 border-neutral-200 hover:border-accent-300'}`}>{uf}</button>
              })}
            </div>
          </Secao>

          <Secao icon={Package} titulo={`Produtos Oferecidos (${(sel.produtos || []).length})`}>
            <div className="flex flex-wrap gap-2 mb-3">
              {PRODUTOS_PADRAO.map((p) => <button key={p.chave} onClick={() => addProduto(p)} className="text-xs font-semibold border border-accent-200 text-accent-700 rounded-lg px-3 py-1.5 hover:bg-accent-50">+ {p.nome}</button>)}
            </div>
            <div className="space-y-1">
              {(sel.produtos || []).map((p, i) => (
                <div key={i} className="flex items-center gap-2 bg-neutral-50 rounded-lg px-3 py-2 text-sm">
                  <span className="flex-1">{p.nome}</span>
                  <button onClick={() => set('produtos', sel.produtos.filter((_, j) => j !== i))} className="text-red-500"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          </Secao>

          <Secao icon={Percent} titulo={`Regras Comerciais por Estado (${(sel.regras_estado || []).length})`}>
            <div className="space-y-3">
              {(sel.regras_estado || []).map((r, i) => (
                <div key={i} className="border border-neutral-200 rounded-xl p-3 grid sm:grid-cols-4 gap-2">
                  <div><label className={lbl}>UF</label>
                    <select className={inp} value={r.estado || ''} onChange={(e) => setRegra(i, 'estado', e.target.value)}>
                      <option value="">—</option>{UFS.map((u) => <option key={u} value={u}>{u}</option>)}
                    </select>
                  </div>
                  <div><label className={lbl}>Tipo de Acordo</label><input className={inp} value={r.tipo_acordo || ''} onChange={(e) => setRegra(i, 'tipo_acordo', e.target.value)} /></div>
                  <div><label className={lbl}>% Desc. Máximo</label><input type="number" className={inp} value={r.desconto_maximo ?? ''} onChange={(e) => setRegra(i, 'desconto_maximo', e.target.value)} /></div>
                  <div><label className={lbl}>% Desc. Cliente</label><input type="number" className={inp} value={r.desconto_cliente ?? ''} onChange={(e) => setRegra(i, 'desconto_cliente', e.target.value)} /></div>
                  <div><label className={lbl}>% Part. The Conect</label><input type="number" className={inp} value={r.part_theconect ?? ''} onChange={(e) => setRegra(i, 'part_theconect', e.target.value)} /></div>
                  <div><label className={lbl}>% Part. Gerador</label><input type="number" className={inp} value={r.part_gerador ?? ''} onChange={(e) => setRegra(i, 'part_gerador', e.target.value)} /></div>
                  <div><label className={lbl}>Vigência Início</label><input type="date" className={inp} value={r.vigencia_inicial || ''} onChange={(e) => setRegra(i, 'vigencia_inicial', e.target.value)} /></div>
                  <div><label className={lbl}>Vigência Fim</label><input type="date" className={inp} value={r.vigencia_final || ''} onChange={(e) => setRegra(i, 'vigencia_final', e.target.value)} /></div>
                  <label className="flex items-center gap-1.5 text-xs text-neutral-600"><input type="checkbox" checked={!!r.permite_campanhas} onChange={(e) => setRegra(i, 'permite_campanhas', e.target.checked)} /> Permite campanhas</label>
                  <label className="flex items-center gap-1.5 text-xs text-neutral-600"><input type="checkbox" checked={!!r.permite_desconto_personalizado} onChange={(e) => setRegra(i, 'permite_desconto_personalizado', e.target.checked)} /> Desc. personalizado</label>
                  <div><label className={lbl}>Status</label>
                    <select className={inp} value={r.status || 'ativa'} onChange={(e) => setRegra(i, 'status', e.target.value)}><option value="ativa">Ativa</option><option value="inativa">Inativa</option></select>
                  </div>
                  <div className="flex items-end"><button onClick={() => delRegra(i)} className="text-red-500 inline-flex items-center gap-1 text-xs"><Trash2 size={14} /> Remover</button></div>
                </div>
              ))}
              <button onClick={addRegra} className="text-sm font-semibold text-accent-700 inline-flex items-center gap-1"><Plus size={15} /> Adicionar regra de estado</button>
            </div>
          </Secao>

          <Secao icon={Gauge} titulo={`Descontos por Faixa de Consumo (${(sel.faixas_consumo || []).length})`}>
            <div className="space-y-2">
              {(sel.faixas_consumo || []).map((f, i) => (
                <div key={i} className="grid grid-cols-4 gap-2 items-end">
                  <div><label className={lbl}>De (kWh)</label><input type="number" className={inp} value={f.min_kwh ?? ''} onChange={(e) => setFaixa(i, 'min_kwh', e.target.value)} /></div>
                  <div><label className={lbl}>Até (kWh)</label><input type="number" className={inp} value={f.max_kwh ?? ''} onChange={(e) => setFaixa(i, 'max_kwh', e.target.value)} placeholder="∞" /></div>
                  <div><label className={lbl}>% Desconto</label><input type="number" className={inp} value={f.desconto_pct ?? ''} onChange={(e) => setFaixa(i, 'desconto_pct', e.target.value)} /></div>
                  <button onClick={() => delFaixa(i)} className="text-red-500 mb-2"><Trash2 size={15} /></button>
                </div>
              ))}
              <button onClick={addFaixa} className="text-sm font-semibold text-accent-700 inline-flex items-center gap-1"><Plus size={15} /> Adicionar faixa</button>
            </div>
          </Secao>

          <Secao icon={Gauge} titulo="Regras Operacionais">
            <div className="grid sm:grid-cols-3 gap-3">
              <div><label className={lbl}>Tipo de Fatura</label>
                <select className={inp} value={sel.operacional?.tipo_fatura || 'unificada'} onChange={(e) => setObj('operacional', 'tipo_fatura', e.target.value)}>{TIPO_FATURA.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
              </div>
              <div><label className={lbl}>Consumo Mínimo (kWh)</label><input type="number" className={inp} value={sel.operacional?.consumo_minimo_kwh ?? ''} onChange={(e) => setObj('operacional', 'consumo_minimo_kwh', e.target.value)} /></div>
              <div><label className={lbl}>Valor Mínimo Fatura (R$)</label><input type="number" className={inp} value={sel.operacional?.valor_minimo_fatura ?? ''} onChange={(e) => setObj('operacional', 'valor_minimo_fatura', e.target.value)} /></div>
              <div><label className={lbl}>Prazo Médio Injeção (dias)</label><input type="number" className={inp} value={sel.operacional?.prazo_medio_injecao_dias ?? ''} onChange={(e) => setObj('operacional', 'prazo_medio_injecao_dias', e.target.value)} /></div>
              <label className="flex items-center gap-2 text-sm text-neutral-700 mt-5"><input type="checkbox" checked={!!sel.operacional?.troca_titularidade} onChange={(e) => setObj('operacional', 'troca_titularidade', e.target.checked)} /> Com troca de titularidade</label>
              <label className="flex items-center gap-2 text-sm text-neutral-700 mt-5"><input type="checkbox" checked={!!sel.operacional?.requer_analise_credito} onChange={(e) => setObj('operacional', 'requer_analise_credito', e.target.checked)} /> Requer análise de crédito</label>
            </div>
            <div className="mt-3"><label className={lbl}>Tipos de Cliente Aceitos</label>
              <div className="flex gap-2">{TIPOS_CLIENTE.map(([v, l]) => {
                const on = (sel.operacional?.tipos_cliente || []).includes(v)
                return <button key={v} onClick={() => toggleTipoCli(v)} className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${on ? 'bg-accent-500 text-white border-accent-500' : 'bg-white text-neutral-500 border-neutral-200'}`}>{l}</button>
              })}</div>
            </div>
          </Secao>

          <Secao icon={Landmark} titulo="Dados Financeiros">
            <div className="grid sm:grid-cols-2 gap-3">
              {[['datas_pagamento', 'Datas de Pagamento'], ['prazo_pagamento', 'Prazo de Pagamento'], ['forma_pagamento', 'Forma de Pagamento'], ['dados_bancarios', 'Dados Bancários'], ['percentual_retencao', '% Retenção'], ['obs', 'Observações Financeiras']].map(([k, l]) => (
                <div key={k}><label className={lbl}>{l}</label><input className={inp} value={sel.financeiro?.[k] ?? ''} onChange={(e) => setObj('financeiro', k, e.target.value)} /></div>
              ))}
            </div>
          </Secao>

          <Secao icon={FileText} titulo="Dados para Nota Fiscal">
            <div className="grid sm:grid-cols-2 gap-3">
              {[['razao_social', 'Razão Social'], ['cnpj', 'CNPJ'], ['inscricao_estadual', 'Inscrição Estadual'], ['endereco', 'Endereço Fiscal'], ['municipio', 'Município'], ['estado', 'Estado'], ['cep', 'CEP'], ['email', 'E-mail Fiscal'], ['natureza_operacao', 'Natureza da Operação'], ['codigo_servico', 'Código do Serviço']].map(([k, l]) => (
                <div key={k}><label className={lbl}>{l}</label><input className={inp} value={sel.fiscal_nf?.[k] ?? ''} onChange={(e) => setObj('fiscal_nf', k, e.target.value)} /></div>
              ))}
            </div>
          </Secao>

          <Secao icon={Plug} titulo="Integração / API">
            <div className="grid sm:grid-cols-2 gap-3">
              <div><label className={lbl}>URL da API</label><input className={inp} value={sel.api_config?.url ?? ''} onChange={(e) => setObj('api_config', 'url', e.target.value)} /></div>
              <div><label className={lbl}>Webhook</label><input className={inp} value={sel.api_config?.webhook ?? ''} onChange={(e) => setObj('api_config', 'webhook', e.target.value)} /></div>
              <div><label className={lbl}>Usuário</label><input className={inp} value={sel.api_config?.usuario ?? ''} onChange={(e) => setObj('api_config', 'usuario', e.target.value)} /></div>
              <div><label className={lbl}>Senha</label><input type="password" className={inp} value={sel.api_config?.senha ?? ''} onChange={(e) => setObj('api_config', 'senha', e.target.value)} placeholder="••••••••" /></div>
              <div><label className={lbl}>Token</label><input type="password" className={inp} value={sel.api_config?.token ?? ''} onChange={(e) => setObj('api_config', 'token', e.target.value)} placeholder="••••••••" /></div>
              <div><label className={lbl}>Ambiente</label>
                <select className={inp} value={sel.api_config?.ambiente || 'producao'} onChange={(e) => setObj('api_config', 'ambiente', e.target.value)}><option value="producao">Produção</option><option value="homologacao">Homologação</option></select>
              </div>
            </div>
            <p className="text-[11px] text-neutral-400 mt-2">Token/senha ficam mascarados; deixe em ••• para não alterar.</p>
          </Secao>

          {sel.id && (
            <Secao icon={BarChart3} titulo="Dashboard do Gerador">
              <button onClick={verDashboard} className="text-sm font-semibold text-accent-700 inline-flex items-center gap-1 mb-3"><RefreshCw size={14} /> Carregar indicadores</button>
              {dash && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[['Clientes', dash.total_clientes], ['Em Análise', dash.clientes_em_analise], ['Conectados', dash.clientes_conectados], ['Ativos', dash.clientes_ativos], ['Taxa Aprovação', dash.taxa_aprovacao + '%'], ['Taxa Reprovação', dash.taxa_reprovacao + '%'], ['Tempo Conexão', (dash.tempo_medio_conexao_dias ?? '—') + 'd'], ['Receita Recorrente', money(dash.receita_recorrente)], ['Volume Energia', (dash.volume_energia_kwh ?? 0) + ' kWh']].map(([l, v]) => (
                    <div key={l} className="bg-neutral-50 rounded-xl p-3"><p className="text-[11px] text-neutral-500">{l}</p><p className="text-lg font-heading font-bold text-graphite">{v}</p></div>
                  ))}
                </div>
              )}
            </Secao>
          )}

          {(sel.historico || []).length > 0 && (
            <Secao icon={History} titulo={`Histórico de Alterações (${sel.historico.length})`}>
              <div className="space-y-1 max-h-64 overflow-y-auto text-xs">
                {[...sel.historico].reverse().map((h, i) => (
                  <div key={i} className="flex gap-2 text-neutral-600 border-b border-neutral-100 py-1">
                    <span className="text-neutral-400">{(h.data || '').slice(0, 16).replace('T', ' ')}</span>
                    <span className="font-semibold">{h.usuario}</span>
                    <span>alterou <b>{h.campo}</b>{h.motivo ? ` — ${h.motivo}` : ''}</span>
                  </div>
                ))}
              </div>
            </Secao>
          )}
        </div>
      )}
    </div>
  )
}
